import React, { useEffect, useState } from 'react';
import API from '../../api';

export default function AdminContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/contacts/admin');
      setContacts(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch messages');
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      (contact.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (contact.subject || '').toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await API.delete(`/contacts/${id}`);
      setContacts(contacts.filter((c) => c._id !== id));
      setSelectedMessage(null);
      alert('Message deleted successfully');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete message');
    }
  };

  return (
    <div style={pageContainer}>
      <div style={contentWrapper}>
        {/* Header */}
        <div style={headerSection}>
          <div>
            <h1 style={pageTitle}>📬 Contact Messages</h1>
            <p style={pageSubtitle}>
              View and manage all customer inquiries and feedback
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={statsContainer}>
          <div style={statCard}>
            <div style={statIcon('#667eea')}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <div style={statNumber}>{contacts.length}</div>
              <div style={statLabel}>Total Messages</div>
            </div>
          </div>

          <div style={statCard}>
            <div style={statIcon('#28a745')}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <div style={statNumber}>{filteredContacts.length}</div>
              <div style={statLabel}>Filtered Results</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={controlsSection}>
          <div style={searchContainer}>
            <svg style={searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={searchInput}
            />
          </div>
        </div>

        {/* Content */}
        <div style={tableContainer}>
          {loading ? (
            <div style={loadingContainer}>
              <div style={spinner}></div>
              <p style={loadingText}>Loading messages...</p>
            </div>
          ) : error ? (
            <div style={errorContainer}>
              <svg style={errorIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 style={errorTitle}>Error Loading Messages</h3>
              <p style={errorText}>{error}</p>
              <button onClick={fetchContacts} style={retryButton}>
                Try Again
              </button>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div style={emptyContainer}>
              <svg style={emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 style={emptyTitle}>No Messages Found</h3>
              <p style={emptyText}>
                {searchTerm ? 'Try adjusting your search criteria' : 'No contact messages yet'}
              </p>
            </div>
          ) : (
            <div style={tableWrapper}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>Name</th>
                    <th style={th}>Email</th>
                    <th style={th}>Subject</th>
                    <th style={th}>Preview</th>
                    <th style={th}>Date</th>
                    <th style={{ ...th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map((contact) => (
                    <tr 
                      key={contact._id} 
                      style={tr}
                      onClick={() => setSelectedMessage(contact)}
                    >
                      <td style={td}>
                        <div style={nameCell}>
                          <div style={avatar}>
                            {(contact.name || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span style={nameText}>{contact.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td style={td}>
                        <a 
                          href={`mailto:${contact.email}`} 
                          style={emailLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contact.email || 'No email'}
                        </a>
                      </td>
                      <td style={td}>
                        <span style={subjectBadge}>
                          {contact.subject || 'No Subject'}
                        </span>
                      </td>
                      <td style={td}>
                        <div style={messageCell}>
                          {contact.message && contact.message.length > 60
                            ? `${contact.message.substring(0, 60)}...`
                            : contact.message || 'No message'}
                        </div>
                      </td>
                      <td style={td}>
                        <div style={dateCell}>
                          <div style={dateText}>
                            {new Date(contact.createdAt).toLocaleDateString()}
                          </div>
                          <div style={timeText}>
                            {new Date(contact.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(contact._id);
                          }}
                          style={deleteButton}
                          title="Delete message"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageModal 
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          onDelete={handleDelete}
        />
      )}

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
}

// Message Detail Modal Component
function MessageModal({ message, onClose, onDelete }) {
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div style={modalHeader}>
          <h2 style={modalTitle}>Message Details</h2>
          <button onClick={onClose} style={closeButton}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 24, height: 24 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div style={modalBody}>
          {/* Sender Info */}
          <div style={senderSection}>
            <div style={senderAvatar}>
              {(message.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={senderName}>{message.name || 'Unknown'}</div>
              <a href={`mailto:${message.email}`} style={senderEmail}>
                {message.email || 'No email'}
              </a>
            </div>
          </div>

          {/* Subject */}
          <div style={detailSection}>
            <label style={detailLabel}>Subject</label>
            <div style={detailValue}>{message.subject || 'No Subject'}</div>
          </div>

          {/* Date */}
          <div style={detailSection}>
            <label style={detailLabel}>Received On</label>
            <div style={detailValue}>
              {new Date(message.createdAt).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>

          {/* Message */}
          <div style={detailSection}>
            <label style={detailLabel}>Message</label>
            <div style={messageContent}>
              {message.message || 'No message content'}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div style={modalFooter}>
          <button onClick={onClose} style={cancelButton}>
            Close
          </button>
          <button 
            onClick={() => {
              onDelete(message._id);
            }} 
            style={deleteModalButton}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Message
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Styles ----------
const pageContainer = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  padding: '40px 20px',
  fontFamily: "'Inter', -apple-system, sans-serif",
};

const contentWrapper = {
  maxWidth: 1400,
  margin: '0 auto',
};

const headerSection = {
  marginBottom: 32,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const pageTitle = {
  fontSize: 36,
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: 8,
  margin: 0,
};

const pageSubtitle = {
  fontSize: 16,
  color: '#718096',
  margin: 0,
};

const statsContainer = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: 20,
  marginBottom: 30,
};

const statCard = {
  background: '#fff',
  padding: 28,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'default',
};

const statIcon = (color) => ({
  width: 56,
  height: 56,
  borderRadius: 12,
  background: `${color}15`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
  flexShrink: 0,
});

const statNumber = {
  fontSize: 32,
  fontWeight: 700,
  color: '#2d3748',
  lineHeight: 1,
};

const statLabel = {
  fontSize: 14,
  color: '#718096',
  marginTop: 6,
  fontWeight: 500,
};

const controlsSection = {
  marginBottom: 24,
};

const searchContainer = {
  position: 'relative',
  maxWidth: 500,
};

const searchIcon = {
  position: 'absolute',
  left: 18,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 20,
  height: 20,
  color: '#a0aec0',
  pointerEvents: 'none',
};

const searchInput = {
  width: '100%',
  padding: '16px 16px 16px 52px',
  fontSize: 15,
  border: '2px solid #e2e8f0',
  borderRadius: 12,
  outline: 'none',
  background: '#fff',
  transition: 'all 0.3s ease',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const tableContainer = {
  background: '#fff',
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.05)',
};

const tableWrapper = {
  overflowX: 'auto',
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
};

const th = {
  padding: '18px 20px',
  textAlign: 'left',
  fontSize: 13,
  fontWeight: 700,
  color: '#4a5568',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  background: '#f7fafc',
  borderBottom: '2px solid #e2e8f0',
};

const tr = {
  borderBottom: '1px solid #e2e8f0',
  transition: 'background 0.2s ease',
  cursor: 'pointer',
};

const td = {
  padding: '20px',
  fontSize: 14,
  color: '#4a5568',
  verticalAlign: 'middle',
};

const nameCell = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const avatar = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 16,
  flexShrink: 0,
};

const nameText = {
  fontWeight: 600,
  color: '#2d3748',
  fontSize: 15,
};

const emailLink = {
  color: '#667eea',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  fontWeight: 500,
};

const subjectBadge = {
  display: 'inline-block',
  padding: '8px 14px',
  background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  color: '#667eea',
  border: '1px solid #667eea30',
};

const messageCell = {
  maxWidth: 300,
  lineHeight: 1.6,
  color: '#718096',
};

const dateCell = {
  whiteSpace: 'nowrap',
};

const dateText = {
  fontWeight: 600,
  color: '#2d3748',
  marginBottom: 4,
  fontSize: 14,
};

const timeText = {
  fontSize: 12,
  color: '#a0aec0',
  fontWeight: 500,
};

const deleteButton = {
  padding: 10,
  background: '#fff',
  border: '2px solid #e2e8f0',
  borderRadius: 8,
  color: '#e53e3e',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const loadingContainer = {
  padding: 80,
  textAlign: 'center',
};

const spinner = {
  width: 48,
  height: 48,
  border: '4px solid #e2e8f0',
  borderTop: '4px solid #667eea',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
  margin: '0 auto 20px',
};

const loadingText = {
  color: '#718096',
  fontSize: 15,
  fontWeight: 500,
};

const errorContainer = {
  padding: 80,
  textAlign: 'center',
};

const errorIcon = {
  width: 56,
  height: 56,
  color: '#e53e3e',
  margin: '0 auto 20px',
};

const errorTitle = {
  fontSize: 22,
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: 12,
};

const errorText = {
  color: '#718096',
  marginBottom: 24,
  fontSize: 15,
};

const retryButton = {
  padding: '12px 32px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const emptyContainer = {
  padding: 80,
  textAlign: 'center',
};

const emptyIcon = {
  width: 56,
  height: 56,
  color: '#cbd5e0',
  margin: '0 auto 20px',
};

const emptyTitle = {
  fontSize: 22,
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: 12,
};

const emptyText = {
  color: '#718096',
  fontSize: 15,
};

// Modal Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 20,
  animation: 'fadeIn 0.2s ease',
  backdropFilter: 'blur(4px)',
};

const modalContent = {
  background: '#fff',
  borderRadius: 20,
  maxWidth: 700,
  width: '100%',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  animation: 'slideUp 0.3s ease',
};

const modalHeader = {
  padding: '28px 32px',
  borderBottom: '2px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

const modalTitle = {
  fontSize: 24,
  fontWeight: 700,
  color: '#fff',
  margin: 0,
};

const closeButton = {
  background: 'rgba(255,255,255,0.2)',
  border: 'none',
  borderRadius: 8,
  padding: 8,
  cursor: 'pointer',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.2s ease',
};

const modalBody = {
  padding: 32,
};

const senderSection = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 28,
  padding: 20,
  background: '#f7fafc',
  borderRadius: 12,
};

const senderAvatar = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 24,
  flexShrink: 0,
};

const senderName = {
  fontSize: 20,
  fontWeight: 700,
  color: '#2d3748',
  marginBottom: 4,
};

const senderEmail = {
  color: '#667eea',
  textDecoration: 'none',
  fontSize: 15,
  fontWeight: 500,
};

const detailSection = {
  marginBottom: 24,
};

const detailLabel = {
  fontSize: 13,
  fontWeight: 700,
  color: '#718096',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 8,
  display: 'block',
};

const detailValue = {
  fontSize: 15,
  color: '#2d3748',
  lineHeight: 1.6,
  fontWeight: 500,
};

const messageContent = {
  fontSize: 15,
  color: '#4a5568',
  lineHeight: 1.8,
  padding: 20,
  background: '#f7fafc',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const modalFooter = {
  padding: '24px 32px',
  borderTop: '2px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  background: '#fafbfc',
};

const cancelButton = {
  padding: '12px 24px',
  background: '#fff',
  border: '2px solid #e2e8f0',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  color: '#4a5568',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const deleteModalButton = {
  padding: '12px 24px',
  background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  transition: 'transform 0.2s ease',
};